import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
    Loader2,
    MessageCircleQuestion,
    Send,
    CheckCircle2,
    Clock,
    Mail,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminQuestions() {
    const { user, token, loading } = useAuth();
    const [busy, setBusy] = useState(true);
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("open");
    const [drafts, setDrafts] = useState({});
    const [sending, setSending] = useState({});

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () => {
        setBusy(true);
        const url =
            filter === "all"
                ? `${API}/admin/questions`
                : `${API}/admin/questions?status=${filter}`;
        return axios
            .get(url, { headers })
            .then((r) => setItems(r.data || []))
            .catch(() => toast.error("Could not load questions"))
            .finally(() => setBusy(false));
    };

    useEffect(() => {
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, filter]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!user) return <Navigate to="/admin/login" replace />;

    const setDraft = (id, val) =>
        setDrafts((s) => ({ ...s, [id]: val }));

    const submitAnswer = async (id) => {
        const text = (drafts[id] || "").trim();
        if (!text) {
            toast.error("Type an answer first");
            return;
        }
        setSending((s) => ({ ...s, [id]: true }));
        try {
            await axios.post(
                `${API}/admin/questions/${id}/answer`,
                { answer_body: text },
                { headers },
            );
            toast.success("Answer sent — client emailed");
            setDrafts((s) => ({ ...s, [id]: "" }));
            refresh();
        } catch {
            toast.error("Could not send answer");
        } finally {
            setSending((s) => ({ ...s, [id]: false }));
        }
    };

    const counts = {
        open: items.filter((i) => i.status === "open").length,
        answered: items.filter((i) => i.status === "answered").length,
    };

    return (
        <AdminShell>
            <div className="max-w-5xl">
                <div className="flex items-center gap-2 mb-3">
                    <MessageCircleQuestion className="w-4 h-4 text-ember" />
                    <p className="text-xs uppercase tracking-[0.25em] text-ember">
                        Briefcase · Q&A inbox
                    </p>
                </div>
                <h1 className="font-display text-bone text-5xl tracking-tightest leading-[0.95] mb-10">
                    Client questions.
                </h1>

                <div className="flex items-center gap-2 mb-8 border-b border-white/10 pb-3 flex-wrap">
                    {[
                        { v: "open", label: `Open · ${counts.open}` },
                        {
                            v: "answered",
                            label: `Answered · ${counts.answered}`,
                        },
                        { v: "all", label: "All" },
                    ].map((t) => (
                        <button
                            key={t.v}
                            onClick={() => setFilter(t.v)}
                            data-testid={`filter-${t.v}`}
                            className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                filter === t.v
                                    ? "border-ember text-ember"
                                    : "border-white/15 text-bone-300 hover:border-white/30"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {busy ? (
                    <div className="text-bone-500 text-sm flex items-center gap-3">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading…
                    </div>
                ) : items.length === 0 ? (
                    <p className="text-bone-500 text-sm border border-dashed border-white/10 p-12 text-center">
                        No {filter === "all" ? "" : filter} questions.
                    </p>
                ) : (
                    <ul
                        data-testid="admin-questions-list"
                        className="space-y-5"
                    >
                        {items.map((q) => (
                            <li
                                key={q.id}
                                data-testid={`admin-question-${q.id}`}
                                className="border border-white/10 bg-ink-800/40 p-6"
                            >
                                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                                    <div>
                                        <p className="font-display text-bone text-lg tracking-tight">
                                            {q.client_name}
                                            {q.client_company && (
                                                <span className="text-bone-500 font-sans text-sm font-normal ml-2">
                                                    · {q.client_company}
                                                </span>
                                            )}
                                        </p>
                                        <a
                                            href={`mailto:${q.client_email}`}
                                            className="text-xs text-bone-500 hover:text-ember transition-colors inline-flex items-center gap-1"
                                        >
                                            <Mail className="w-3 h-3" />
                                            {q.client_email}
                                        </a>
                                    </div>
                                    <span
                                        className={`text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 px-2 py-1 border ${
                                            q.status === "answered"
                                                ? "border-emerald-400/40 text-emerald-400"
                                                : "border-amber-400/40 text-amber-400"
                                        }`}
                                    >
                                        {q.status === "answered" ? (
                                            <CheckCircle2 className="w-3 h-3" />
                                        ) : (
                                            <Clock className="w-3 h-3" />
                                        )}
                                        {q.status}
                                    </span>
                                </div>

                                <p className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mb-2">
                                    {q.project_title
                                        ? `About: ${q.project_title}`
                                        : "General"}{" "}
                                    · {new Date(q.created_at).toLocaleString()}
                                </p>

                                <p className="text-bone text-sm leading-relaxed whitespace-pre-wrap mb-4">
                                    {q.body}
                                </p>

                                {q.answer_body ? (
                                    <div className="border-l-2 border-ember pl-4">
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-ember mb-2">
                                            Your answer ·{" "}
                                            {q.answered_at
                                                ? new Date(
                                                      q.answered_at,
                                                  ).toLocaleString()
                                                : ""}
                                        </p>
                                        <p className="text-bone-300 text-sm leading-relaxed whitespace-pre-wrap">
                                            {q.answer_body}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <textarea
                                            value={drafts[q.id] || ""}
                                            onChange={(e) =>
                                                setDraft(q.id, e.target.value)
                                            }
                                            rows={3}
                                            placeholder="Write a thoughtful answer…"
                                            data-testid={`answer-input-${q.id}`}
                                            className="w-full bg-transparent border border-white/10 focus:border-ember outline-none p-3 text-bone placeholder:text-bone-500 text-sm transition-colors resize-none"
                                        />
                                        <button
                                            onClick={() => submitAnswer(q.id)}
                                            disabled={sending[q.id]}
                                            data-testid={`answer-submit-${q.id}`}
                                            className="btn-ember inline-flex items-center gap-2 h-10 px-5 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
                                        >
                                            {sending[q.id] ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Send className="w-4 h-4" />
                                                    Send answer
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </AdminShell>
    );
}
